import { FunctionService } from 'src/app/services/general/function.service';
import { Component, ElementRef, ViewChild, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationExtras, ActivatedRoute, Params } from '@angular/router';
import { ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { AccountsListService } from 'src/app/services/accounts/accounts-list.service';
import { OnlineService } from 'src/app/services/general/online.service';
import { PopupService } from 'src/app/services/general/popup.service';

import { AccountsAddType, AccountsListType } from 'src/app/types/accounts';
import { AccountInfo, UserAccounts } from 'src/app/interfaces/account';
import { PromoService } from 'src/app/services/general/promo.service';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DateTimeService } from 'src/app/services/general/date-time.service';
import { User } from 'src/app/interfaces/user';
import { UserService } from 'src/app/services/user/user.service';
import { LinkService } from 'src/app/services/general/link.service';
import { RootMenuList } from 'src/app/commons/menu';
import { Menu } from 'src/app/interfaces/menu';
import { GreetingService } from 'src/app/services/general/greeting.service';
import { AccountsAddComponent } from 'src/app/components/accounts/accounts-add/accounts-add.component';

/**
 * Accounts List component.
 * List of user logged account.
 * Search & filter account list.
 */
@Component({
  selector: 'app-accounts-list-page',
  templateUrl: './accounts-list.page.html',
  styleUrls: ['./accounts-list.page.scss'],
})
export class AccountsListPage implements OnInit, OnDestroy {

  @ViewChild(CdkVirtualScrollViewport) cdkVirtualScrollViewport: CdkVirtualScrollViewport;
  
  @ViewChild('content', { read: ElementRef }) contentElement: ElementRef;
  /**
   * Header for getting header height
   */
  @ViewChild('header', { read: ElementRef }) header: ElementRef;

  showSearch: boolean;

  ready: boolean;
  /**
   * Reset search
   */
  resetSearch: boolean;
  /**
   * Accounts Existed
   */
  accountExisted: boolean;
  /**
   * User account list
   */
  userAccountsList: UserAccounts[];
  /**
   * Accounts list type - all, upcoming, passed
   */
  accountsListType: AccountsListType;

  /**
   * Search Term
   */
  searchTerm: string;

  countDownList: { [accountId: string]: number | string };

  user: User;

  online: boolean;

  menuList: Menu[] = RootMenuList;

  greetingMsg: string;

  /**
   * Filter
   */
  private filter: any;
  /**
   * User account list subscription
   */
  private userAccountsListSubscription: Subscription;

  private onlineSubscription: Subscription;

  private userSubscription: Subscription;

  // private showSearchSubscription: Subscription;

  /**
   * Constructor
   * @param router Router
   * @param modalController Modal Controller
   * @param actionSheetController Action Sheet Controller
   * @param translate Translate
   * @param accountService Account Service
   * @param accountsListService Accounts List Service
   * @param onlineService Onilne Service
   * @param popupService Popup Service
   */
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private platform: Platform,
    private translate: TranslateService,
    private modalController: ModalController,
    private accountsListService: AccountsListService,
    private dateTimeService: DateTimeService,
    private promoService: PromoService,
    private greetingService: GreetingService,
    private onlineService: OnlineService,
    private userService: UserService,
    private linkService: LinkService,
    private popupService: PopupService,
    private functionService: FunctionService,
  ) { }

  ngOnInit() {
    this.watchRoute();
  }

  ngOnDestroy(): void {
    this.unwatchUserAccountsList();
    this.unwatchOnline();
    this.unwatchUser();
    // this.unwatchShowSearch();
  }

  watchRoute() {
    this.route.params.subscribe((param: Params) => {
      const route = this.router.getCurrentNavigation();
      const state = route?.extras?.state;
      if (param?.addNew) {
        this.presentAccountsAddModal();
      } else if (state?.addNew) {
        this.presentAccountsAddModal();
      }
    });
  }

  /**
   * Before view enter
   */
  async ionViewWillEnter() {
    this.resetSearch = true;
    this.ready = true;
    this.accountsListType = 'all';
    this.showSearch = false;
    
    this.greetingMsg = this.translate.instant('GREETING.' + this.greetingService.getGreeting(), { name: this.user?.name });
    this.watchOnline();
    this.watchUser();
    // this.watchShowSearch();
  }

  async ionViewDidEnter() {
    await this.platform.ready();
    await this.popupService.presentLoading();
   
    if (this.accountsListService?.checked || this.userAccountsList?.length) {
      this.popupService.dismissLoading();
    } else {
      await this.accountsListService.readUserAccountsList();
    }
    await this.watchUserAccountsList();
  }

  /**
   * Before view leave
   */
  ionViewWillLeave() {
    this.resetSearch = false;
    this.ready = false;
    this.accountsListService.unwatchUserAccountsList();
    this.unwatchUserAccountsList();
    this.unwatchOnline();
    this.unwatchUser();
    // this.unwatchShowSearch();
  }

  /**
   * Watch account list
   */
  async watchUserAccountsList() {
    if (!this.userAccountsListSubscription) {
      this.userAccountsListSubscription = this.accountsListService.observableUserAccountsList
      .subscribe(async (userAccountsList: UserAccounts[]) => {
        if (userAccountsList?.length) {
          await this.loadAccountsList();
          this.accountExisted = true;
        } else {
          this.accountExisted = false;
          // this.checkUserWelcome();
        }
        this.setupCountDownList();
        if (this.accountsListService.checked) {
          await this.popupService.dismissLoading();
          await this.functionService.delay(3000);
          this.promoService.checkPromoBanner();
        }
      });
    }
  }

  /**
   * Unwatch account list
   */
  async unwatchUserAccountsList() {
    if (this.userAccountsListSubscription) {
      this.userAccountsListSubscription.unsubscribe();
      this.userAccountsListSubscription = null;
    }
  }

  async watchOnline() {
    if (!this.onlineSubscription) {
      this.onlineSubscription = this.onlineService.observableOnline.subscribe((online: boolean) => {
        this.online = online;
      });
    }
    
  }

  async unwatchOnline() {
    if (this.onlineSubscription) {
      this.onlineSubscription.unsubscribe();
      this.onlineSubscription = null;
    }
  }

  async watchUser() {
    if (!this.userSubscription) {
      this.userSubscription = this.userService.observableUser.subscribe((user: User) => {
        this.user = user;
        this.greetingMsg = this.translate.instant('GREETING.' + this.greetingService.getGreeting(), { name: this.user?.name });
      });
    }
    
  }

  async unwatchUser() {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
      this.userSubscription = null;
    }
  }

  // async watchShowSearch() {
  //   if (!this.showSearchSubscription) {
  //     this.showSearchSubscription = this.accountsListService.observableAccountsListSearchMode.subscribe((showSearch: boolean) => {
  //       this.showSearch = showSearch;
  //     });
  //   }
  // }

  // async unwatchShowSearch() {
  //   if (this.showSearchSubscription) {
  //     this.showSearchSubscription.unsubscribe();
  //     this.showSearchSubscription = null;
  //   }
  // }

  setupViewport() {
    if (this.cdkVirtualScrollViewport) {
      setTimeout(() => {
        this.cdkVirtualScrollViewport?.checkViewportSize();
      }, 200);
    } else {
      setTimeout(() => {
        this.setupViewport();
      }, 200);
    }
  }

  setupCountDownList() {
    if (!this.countDownList) {
      this.countDownList = {};
    }
    this.userAccountsList?.forEach((x: UserAccounts) => {
      if (x?.enable && x?.accountId && x?.time) {
        this.countDownList[x.accountId] = this.getCountDown(x.time);
      }
    });
  }
  /**
   * Search action
   * @param data Search data, keyword & filter
   */
  search(data: any) {
    this.searchTerm = data?.keyword;
    this.filter = data?.filter?.enable ? data.filter : null;
    this.loadAccountsList();
  }

  /**
   * Accounts list type segment changed account
   */
  segmentChanged() {
    this.loadAccountsList();
  }

  /**
   * Load user account list
   */
  async loadAccountsList() {
    this.userAccountsList = this.accountsListService.searchAccountsList(this.searchTerm, this.accountsListType, this.filter);
    this.setupViewport();
  }

  /**
   * Login into account account
   * @param accountId Account ID
   */
  loginAccount(accountInfo: AccountInfo) {
    if (accountInfo.accountType === 'web' && this.platform.is('hybrid')) {
      this.popupService.presentAlert(this.translate.instant('ACCOUNTS_LOGIN.web_access_only'));
    } else if (accountInfo.accountType === 'app' && !this.platform.is('hybrid')) {
      this.popupService.presentAlert(this.translate.instant('ACCOUNTS_LOGIN.app_access_only'));
    } else if (accountInfo?.accountId) {
      this.goAccountLoginPage(accountInfo.accountId);
    }
  }

  /**
   * Check ion-item slider swipe action
   * @param account Account info
   */
  checkActionSwipe(account: UserAccounts) {
    if (account) {
      if (account?.owner) {
        this.popupService.presentToast(this.translate.instant('ACCOUNTS.msg.unable_delete_owner'), 'warning');
      } else {
        if (account?.role?.coupleId) {
          const couple = this.translate.instant('LIST.role.' + account.role.type);
          this.popupService.presentToast(this.translate.instant('ACCOUNTS.msg.unable_delete_wedding_couple', { couple }), 'warning');
        }
      }
    }
  }

  format(timestamp: number, dateTimeFormat: string, timezone?: string) {
    return this.dateTimeService.format(timestamp, dateTimeFormat, '', timezone);
  }

  async goPremiumPage() {
    this.router.navigate(['/accounts/premium']);
  }

  /**
   * Go add account page
   * @param mode Add account mode, default is premium
   */
  async goAddAccount(accountAddType: AccountsAddType = 'premium') {
    if (this.onlineService.isOnline()) {
      const navigationExtras: NavigationExtras = {
        state: {
          accountAddType
        }
      };
      this.router.navigate(['/accounts/add'], navigationExtras);
    }
  }

  async presentAccountsAddModal() {
    const modal = await this.modalController.create({
      component: AccountsAddComponent,
      cssClass: 'modal-transparent modal-login',
      initialBreakpoint: 0.5,
      breakpoints: [0.5, 0.9],
      componentProps: {

      }
    });
    modal.present();
    modal.onDidDismiss().then(() => {

    });
  }

  /**
   * Delete account confirmation prompt
   * @param accountId Account ID
   */
  async deleteAccount(accountId: string) {
    if (accountId) {
      const modal = await this.popupService.presentConfirm(
        this.translate.instant('ACCOUNTS.msg.remove_access')
      );
      modal.onDidDismiss().then((result: any) => {
        if (result?.data?.confirm) {
          this.saveDeleteAccount(accountId);
        }
      });
    } else {
      this.popupService.presentActionError();
    }
  }

  /**
   * Save delete account from user account list
   * @param accountId Account ID
   */
  async saveDeleteAccount(accountId: string) {
    await this.popupService.presentLoading();
    if (accountId) {
      await this.accountsListService.deleteAccountsList(accountId);
    }
    this.popupService.dismissLoading();
  }

  /**
   * Get count down to current date
   * @param time Time
   * @returns count down number
   */
  getCountDown(time: any): any {
    const coundown: number = this.functionService.getCountDown(time);
    return coundown === 0 ? coundown.toString() : coundown;
  }

  getContentHeight(): number {
    return this.contentElement?.nativeElement?.offsetHeight ? this.contentElement.nativeElement.offsetHeight : window?.innerHeight ? window.innerHeight : 0;
  }

  /**
   * check user welcome status
   */
  // async checkUserWelcome() {
  //   const userWelcome = await this.storageService.get('user_welcome');
  //   if (this.userService.user) {
  //     if (!userWelcome && !this.accountsListService?.userAccountsList?.length) {
  //       this.presentUserWelcomeModal();
  //       const result = await this.storageService.set('user_welcome', 'true');
  //     }
  //   } else {
  //     setTimeout(() => {
  //       this.checkUserWelcome();
  //     }, 500);
  //   }
  // }

  // /**
  //  * Present mobile authentication modal
  //  */
  // async presentUserWelcomeModal() {
  //   const modal = await this.modalController.create({
  //     component: UserWelcomeComponent,
  //     cssClass: 'modal-transparent',
  //     componentProps: { }
  //   });
  //   modal.present();
  // }

  /**
   * Go account login page
   * @param accountId Account ID
   */
  async goAccountLoginPage(accountId: string) {
    if (accountId) {
      const navigationExtras: NavigationExtras = {
        state: { accountId, new: true }
      };
      this.router.navigate(['/accounts/login'], navigationExtras);
    } else {
      this.popupService.presentActionError();
    }
  }

  goTrialPage() {
    this.router.navigate(['/subscription/trial']);
  }

  goProfile() {
    this.router.navigate(['/user/profile']);
  }

  goPage(url: string) {
    if (url) {
      if (url === 'help_center') {
        this.linkService.goHelpCenter();
      } else if (url === '/user/logout') {
        this.userLogout();
      } else if (url === '/accounts/logout') {
        this.accountsLogout();
      } else if (url === '/about/app-rating') {
        this.goAppRatingPage();
      } else {
        this.router.navigate([url]);
      }
    }
  }

  goAppRatingPage() {
    const navigationExtras: NavigationExtras = {
      state: { backHelpPage: true, showPage: true }
    };
    this.router.navigate(['/about/app-rating/true'], navigationExtras);
  }

  /**
   * User logout confirmation
   */
  async userLogout() {
    if (this.onlineService.isOnline()) {
      if (this.userService?.user?.isAnonymous) {
        const modal = await this.popupService.presentConfirm(
          this.translate.instant('LOGIN.logout.anonymous_msg'), 'logout'
        );
        modal.onWillDismiss().then((result: any) => {
          if (result?.data?.confirm) {
            this.router.navigate(['/user/logout']);
          }
        });
      } else {
        const modal = await this.popupService.presentConfirm(
          this.translate.instant('LOGIN.logout.confirm_msg'), 'logout'
        );
        modal.onWillDismiss().then((result: any) => {
          if (result?.data?.confirm) {
            this.router.navigate(['/user/logout']);
          }
        });
      }
    }
  }

  async accountsLogout() {
    const modal = await this.popupService.presentConfirm(
      this.translate.instant('ACCOUNT.msg.logout')
    );
    modal.onWillDismiss().then((result: any) => {
      if (result?.data?.confirm) {
        this.router.navigate(['/accounts/logout']);
      }
    });
  }

  toggleSearch() {
    this.showSearch = !this.showSearch;
  }

  /**
   * Calculate item height for virtual scroll
   * @param item Item
   */
  itemHeightFn() {
    return 175;
  }

  /**
   * Track item
   * @param index Index
   * @param item item
   */
  trackByFn(index: number, item: UserAccounts) {
    if (item?.accountId) {
      return item.accountId;
    }
    return index;
  }
}
